/**
 * voice-v8 — Werkzeugkarten: Erinnerung erledigt und Timer mit Ton.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { baueKarte, kartenTitel } from "../karte.js";
import { unwrapResult } from "./hilfe.js";

interface TimerResult {
  seconds?: number;
  label?: string;
  dueAt?: string;
}

export function buildTimerCard(result: unknown): HTMLElement {
  const r = (result ?? {}) as TimerResult;
  const el = baueKarte("timer");
  // 2026-07-02 (Martin): kein doppeltes "TIMER · TIMER" mehr — nur das Label
  // (oder schlicht "Timer"), das Badge sagt bereits, dass es ein Timer ist.
  el.appendChild(kartenTitel(r.label ? String(r.label) : "Timer"));

  const display = document.createElement("div");
  display.className = "tool-timer-display";
  display.textContent = formatRemaining(r.seconds ?? 0);
  el.appendChild(display);

  const dueAtMs = r.dueAt ? new Date(r.dueAt).getTime() : 0;
  if (dueAtMs > 0) {
    // 2026-07-02 (Martin): nur laeuten, wenn der Countdown in DIESER
    // Karten-Instanz gelaufen ist — sonst wuerde jede historische Karte
    // beim Neuladen des Verlaufs den Wecker starten.
    let sawRunning = false;
    const tick = (): void => {
      const remainingSec = Math.max(
        0,
        Math.round((dueAtMs - Date.now()) / 1000),
      );
      display.textContent = formatRemaining(remainingSec);
      if (remainingSec === 0) {
        // 2026-07-02 (Martin): Bezug herstellen — wie lang lief der Timer?
        display.textContent = `fertig · ${formatTimerTotal(r.seconds ?? 0)}`;
        el.classList.add("tool-timer-done");
        if (sawRunning) startTimerAlarm(el);
        return;
      }
      sawRunning = true;
      requestAnimationFrame(() => setTimeout(tick, 250));
    };
    tick();
  }
  return el;
}

// Gesamtdauer menschlich: 30 Sek. / 5:00 Min. / 1:05:00 Std.
function formatTimerTotal(sec: number): string {
  if (sec <= 0) return "";
  if (sec < 60) return `${sec} Sek.`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const ss = String(sec % 60).padStart(2, "0");
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${ss} Std.`;
  return `${m}:${ss} Min.`;
}

function formatRemaining(sec: number): string {
  if (sec <= 0) return "fertig";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0)
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

// 2026-07-02 (Martin): Wecker statt Einzel-Piep — Doppel-Laeuten alle 1,2 s
// (~60 s lang), bis der Stopp-Knopf gedrueckt wird. Vibration wo verfuegbar.
function startTimerAlarm(cardEl: HTMLElement): void {
  let rings = 0;
  const ringOnce = (): void => {
    beep();
    setTimeout(beep, 280);
    try {
      navigator.vibrate?.([220, 120, 220]);
    } catch {
      /* nicht ueberall verfuegbar */
    }
  };
  const iv = window.setInterval(() => {
    rings += 1;
    if (rings > 50) {
      stop();
      return;
    }
    ringOnce();
  }, 1200);
  const stopBtn = document.createElement("button");
  stopBtn.type = "button";
  stopBtn.className = "tool-timer-stop";
  stopBtn.textContent = "Stopp";
  const stop = (): void => {
    window.clearInterval(iv);
    stopBtn.remove();
  };
  stopBtn.addEventListener("click", stop);
  cardEl.appendChild(stopBtn);
  ringOnce();
}

let beepCtx: AudioContext | null = null;
function beep(): void {
  try {
    const Ctx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctx) return;
    if (!beepCtx) beepCtx = new Ctx();
    const ctx = beepCtx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.6);
  } catch {
    /* ignore */
  }
}

// 2026-07-05 (Martin): Erledigt-Knopf an der Chat-Meldung — loest das Ticket
// direkt auf (kein Umweg zur Glocke).
// 2026-07-19 (Traegheits-Jagd): EIN geteilter resolved-Abruf fuer alle
// Reminder-Karten. Vorher holte JEDE Karte beim Bau die komplette
// resolved-Liste einzeln — beim Boot (~26 Karten x 2 Hydrate-Garnituren)
// waren das ~52 identische Abrufe hintereinander. Single-flight + 5s-Frist:
// gleichzeitige Karten teilen sich EINEN Request.
let resolvedIdsFetchedAt = 0;
let resolvedIdsPromise: Promise<Set<number>> | null = null;
function fetchResolvedTicketIds(): Promise<Set<number>> {
  if (resolvedIdsPromise && Date.now() - resolvedIdsFetchedAt < 5000) {
    return resolvedIdsPromise;
  }
  resolvedIdsFetchedAt = Date.now();
  resolvedIdsPromise = (async () => {
    const res = await fetch("/api/tickets?status=resolved", {
      credentials: "include",
    });
    if (!res.ok) return new Set<number>();
    const j = (await res.json()) as { tickets?: Array<{ id: number }> };
    return new Set((j.tickets ?? []).map((t) => t.id));
  })();
  return resolvedIdsPromise;
}

export function buildReminderDoneCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const ticketId =
    typeof r.ticketId === "number" ? r.ticketId : Number(r.ticketId);
  // 2026-07-05 (Martin): in eine echte Karte (bordiert), nicht nackt daneben.
  const el = baueKarte("reminder-done");
  const row = document.createElement("div");
  row.className = "reminder-done-row";
  // 2026-07-08 (Martin): der Glocken-Pill "Erinnerung/Meldung" wirkte wie ein
  // kaputter Knopf und traegt nichts bei — entfernt. Es bleibt der
  // Erledigt-Knopf (rechtsbuendig durch das Row-Layout).
  /*
   * 2026-08-24 (Martin schickte ein Bild: zwei weisse Karten mit NUR dem Wort
   * "Erledigt" und einer Uhrzeit, sonst nichts):
   *
   *   Diese Karte kennt nur die Ticket-Nummer — den Text der Erinnerung nicht.
   *   Der steht in der Nachricht DANEBEN. Fehlt die (weil die Erinnerung schon
   *   abgehakt und aus dem Verlauf verschwunden ist), bleibt eine leere Karte
   *   zurueck.
   *
   *   Sichtbar wurde das erst durch meine Aenderung vom selben Vormittag: Seit
   *   der lassen sich Erinnerungen ueberhaupt abhaken. Der Fehler ist aelter,
   *   nur konnte ihn vorher niemand ausloesen — ein Weg wurde geoeffnet, ohne
   *   den Rueckweg zu pruefen.
   *
   *   Jetzt traegt die Karte immer einen Namen: den mitgereisten Titel, wenn es
   *   einen gibt, sonst schlicht "Erinnerung". Eine Karte ohne Aussage ist
   *   schlimmer als keine Karte.
   */
  const titel = document.createElement("span");
  titel.className = "reminder-done-titel";
  const mitgereist =
    typeof r.signalTitle === "string" ? r.signalTitle.trim() : "";
  titel.textContent = mitgereist || (r.isReminder === false ? "Meldung" : "Erinnerung");
  row.appendChild(titel);

  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "reminder-done-btn";
  btn.textContent = "Erledigt";
  row.appendChild(btn);
  el.appendChild(row);
  if (!Number.isFinite(ticketId)) {
    btn.disabled = true;
    return el;
  }
  // 2026-07-08 (Martin, "in der Glocke erledigt, im Chat nicht"): Status mit
  // der Glocke synchron halten — beim Aufbau pruefen und auf das
  // tickets-changed-Event reagieren (feuert die Glocke beim Erledigen).
  const markDone = (schonBeimAufbau = false) => {
    btn.textContent = "\u2713 Erledigt";
    btn.classList.add("is-done");
    btn.disabled = true;
    /*
     * ⚠️ 2026-08-30 (Martin: *„das andere sind dann diese erledigt Felder —
     * ob wir das dort überhaupt noch brauchen"*).
     *
     * Im Browser gezaehlt: NEUN solcher Karten allein im sichtbaren
     * Verlauf. Die Ursache war eine Ungleichheit, die man nur im Nachhinein
     * sieht: Wer JETZT abhakt, sah die Karte zur Zeile schrumpfen (der
     * Klick-Weg setzt `reminder-done-collapsed`). Wer den Verlauf spaeter
     * neu laedt, bekam dieselbe, laengst erledigte Erinnerung wieder in
     * voller Kartengroesse — derselbe Zustand, zwei Erscheinungsbilder.
     *
     * Eine erledigte Erinnerung ist eine Quittung, kein Aufruf. Sie darf
     * dastehen, aber leise. Deshalb schrumpft die Karte jetzt auf BEIDEN
     * Wegen; nur beim Abhaken von Hand kommt die Rueckgaengig-Zeile dazu.
     */
    el.classList.add("reminder-done-collapsed");
    if (schonBeimAufbau) el.classList.add("reminder-done-alt");
  };
  const syncStatus = async () => {
    if (btn.classList.contains("is-done")) return;
    try {
      const ids = await fetchResolvedTicketIds();
      if (ids.has(ticketId)) markDone(true);
    } catch {
      /* Status-Check best-effort */
    }
  };
  void syncStatus();
  // 2026-07-19 (Traegheits-Jagd): der Listener loeste sich NIE — jede
  // gerenderte Karte liess einen permanenten window-Listener zurueck, der
  // ueber die Closure den abgehaengten Karten-Baum festhielt (~52 nach dem
  // Boot-Doppel-Hydrate, monoton wachsend). Jetzt raeumt sich der Listener
  // beim naechsten Event selbst ab, sobald seine Karte nicht mehr im DOM ist.
  const onTicketsChanged = (): void => {
    if (!btn.isConnected) {
      window.removeEventListener("voice-v8:tickets-changed", onTicketsChanged);
      return;
    }
    void syncStatus();
  };
  window.addEventListener("voice-v8:tickets-changed", onTicketsChanged);
  // 2026-07-11 (Martin, "muss zweimal klicken"): OPTIMISTISCH — EIN Tipp hakt
  // sofort sichtbar ab (Karte schrumpft zur Zeile, 6s Rueckgaengig), der
  // Server laeuft im Hintergrund. Vorher wartete der Knopf still auf die
  // Antwort, was sich wie ein noetiger Bestaetigungs-Zweitklick anfuehlte.
  const patchStatus = async (status: "resolved" | "open"): Promise<boolean> => {
    try {
      const res = await fetch(`/api/tickets/${ticketId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      return res.ok;
    } catch {
      return false;
    }
  };
  btn.addEventListener("click", () => {
    // Sofortiges Abhaken (optimistisch).
    markDone();
    const undo = document.createElement("button");
    undo.type = "button";
    undo.className = "reminder-undo-btn";
    undo.textContent = "Rückgängig";
    row.appendChild(undo);
    const undoTimer = window.setTimeout(() => undo.remove(), 6000);
    undo.addEventListener("click", () => {
      window.clearTimeout(undoTimer);
      undo.remove();
      el.classList.remove("reminder-done-collapsed");
      btn.textContent = "Erledigt";
      btn.classList.remove("is-done");
      btn.disabled = false;
      void patchStatus("open").then((ok) => {
        window.dispatchEvent(new CustomEvent("voice-v8:tickets-changed"));
        if (ok) return;
        // Server hat das Zurücknehmen nicht angenommen — die Karte sagt ehrlich „erledigt“.
        markDone();
        window.dispatchEvent(
          new CustomEvent("voice-v8:toast", {
            detail: { message: "Konnte nicht zurückgenommen werden — die Erinnerung bleibt erledigt.", kind: "info" },
          }),
        );
      });
    });
    void patchStatus("resolved").then((ok) => {
      if (ok) {
        window.dispatchEvent(new CustomEvent("voice-v8:tickets-changed"));
      } else {
        // Server hat nicht mitgespielt — ehrlich zuruecknehmen.
        window.clearTimeout(undoTimer);
        undo.remove();
        el.classList.remove("reminder-done-collapsed");
        btn.textContent = "Erledigt";
        btn.classList.remove("is-done");
        btn.disabled = false;
        window.dispatchEvent(
          new CustomEvent("voice-v8:toast", {
            detail: {
              message:
                "Konnte nicht als erledigt gespeichert werden — bitte nochmal.",
              kind: "info",
            },
          }),
        );
      }
    });
  });
  return el;
}
